/**
 * Provider Service - Abstracted data persistence layer
 * Calls Rails API for all provider operations
 */

import { apiRequest } from '../config/api.js'
import { handleApiError } from './errorHandler.js'

/**
 * Search providers by name
 * @param {string} searchTerm - Search term to match against provider names
 * @param {Object} [options] - Pagination options
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.perPage] - Items per page (default: 20)
 * @returns {Promise<Object|Array>} Object with data array and pagination info, or array for backward compatibility
 */
export async function searchProviders(searchTerm, options = {}) {
  try {
    // If search term is empty, return empty array
    if (!searchTerm || searchTerm.trim().length === 0) {
      return options.page || options.perPage 
        ? { data: [], pagination: { page: 1, per_page: 0, total: 0, total_pages: 0 } }
        : []
    }
    
    // If options provided, use paginated version
    if (options.page || options.perPage) {
      return await getProvidersPaginated({ search: searchTerm.trim(), ...options })
    }
    
    // Otherwise, backward compatible
    const data = await apiRequest(`/providers?search=${encodeURIComponent(searchTerm.trim())}`, {
      method: 'GET'
    })
    
    // Fallback for old format
    return data.data
  } catch (error) {
    handleApiError(error)
    throw error
  }
}


/**
 * Get providers with pagination
 * @param {Object} options - Pagination and filter options
 * @param {string} [options.search] - Optional search term
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.perPage] - Items per page (default: 20)
 * @returns {Promise<Object>} Object with data array and pagination info
 */
export async function getProvidersPaginated({ search = null, page = 1, perPage = 20 } = {}) {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    })
    
    if (search) {
      params.append('search', search)
    }
    
    const data = await apiRequest(`/providers?${params.toString()}`, {
      method: 'GET'
    })
    
    // Handle new paginated format
    if (data.data && data.pagination) {
      return {
        data: data.data,
        pagination: data.pagination
      }
    }
    
    // Fallback for old format (backward compatibility)
    const providers = Array.isArray(data) ? data : (data.providers || [])
    return {
      data: providers,
      pagination: {
        page: 1,
        per_page: providers.length,
        total: providers.length,
        total_pages: 1
      }
    }
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Get a single provider by ID
 * @param {string} providerId - Provider ID
 * @returns {Promise<Object|null>} Provider object or null if not found
 */
export async function getProviderById(providerId) {
  try {
    const data = await apiRequest(`/providers/${providerId}`, {
      method: 'GET'
    })
    return data
  } catch (error) {
    if (error.status === 404) {
      return null
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Create a new provider
 * @param {Object} providerData - Provider object to create
 * @returns {Promise<Object>} Created provider object with ID
 */
export async function createProvider(providerData) {
  try {
    const data = await apiRequest('/providers', {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify({ provider: providerData })
    })
    return data
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Delete a provider
 * @param {string} providerId - Provider ID
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function deleteProvider(providerId) {
  try {
    await apiRequest(`/providers/${providerId}`, {
      method: 'DELETE',
      includeAuth: true
    })
    return true
  } catch (error) {
    if (error.status === 404) {
      return false
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Claim a provider for the current user
 * @param {string} providerId - Provider ID to claim
 * @returns {Promise<Object>} Object containing user, provider, and message
 */
export async function claimProvider(providerId) {
  try {
    const data = await apiRequest(`/providers/${providerId}/claim_provider`, {
      method: 'POST',
      includeAuth: true
    })
    return data
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

